<template>
    <div class="package-card">
        <div class="columns is-mobile mb-0">
            <div class="column pb-0">
                <div class="step-heading">Fill your bundle with courses</div>
            </div>
            <div class="column is-narrow pb-0">
                <div class="alloc-status" :class="statusClass">{{ statusLabel }}</div>
            </div>
        </div>
        <div class="step-sub">Browse the catalog, choose a course, set its clock hours and name, and add it to your bundle — until all {{ pkgTargetHours }} hours are spoken for.</div>

        <div class="multi-note">
            <i class="far fa-layer-group mr-2"></i><strong>Take a course more than once</strong> — up to three times, each with its own focus.
        </div>

        <package-progress class="mt-4 mb-4"/>

        <!-- In the package -->
        <div v-if="pkgSelections.length" class="selected-list">
            <div class="selected-row" v-for="selection in pkgSelections" :key="selection.key">
                <div class="selected-info">
                    <div class="selected-name">{{ selection.name }}<span class="has-text-grey is-size-7 has-text-weight-normal" v-if="selection.named_in_setup"> &middot; named at setup</span></div>
                    <div class="selected-org" v-if="selection.organization">{{ selection.organization }}</div>
                </div>
                <div class="selected-actions">
                    <b-select :value="String(selection.hours)" @input="setPkgCourseHours({key: selection.key, hours: parseInt($event)})" size="is-small">
                        <option v-for="hours in hourOptionsFor(courseById(selection.id))" :key="selection.key + '-h-' + hours" :value="String(hours)">{{ hours }} hours</option>
                    </b-select>
                    <b-button type="is-danger is-light" size="is-small" @click="removePkgCourse(selection.key)">Remove</b-button>
                </div>
            </div>
        </div>

        <div class="has-text-grey mt-4" v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading the catalog...</div>
        <div v-else>

            <!-- Suggested for you -->
            <div v-if="suggested.length" class="browse-section">
                <div class="browse-heading"><i class="fad fa-sparkles mr-2"></i>Suggested for you</div>
                <div class="course-row" v-for="course in suggested" :key="'suggest-' + course.id">
                    <div class="course-info">
                        <div class="course-title">{{ course.title }}</div>
                        <div class="course-org" v-if="course.organization">{{ course.organization.title }}</div>
                    </div>
                    <div class="course-actions">
                        <span class="in-package" v-if="instanceCount(course)">In bundle<template v-if="instanceCount(course) > 1"> &times;{{ instanceCount(course) }}</template></span>
                        <b-button size="is-small" @click="showInfo(course)"><i class="far fa-circle-info mr-2"></i>Info</b-button>
                        <b-button type="is-primary" size="is-small" :disabled="atLimit(course) || fullyAllocated" @click="choose(course)"><i class="fas fa-plus mr-2"></i>Add</b-button>
                    </div>
                </div>
            </div>

            <!-- Browse the catalog -->
            <div class="browse-section">
                <div class="browse-heading"><i class="fad fa-browsers mr-2"></i>Browse the catalog</div>
                <div class="browse-controls">
                    <b-select v-model="categoryFilter" expanded>
                        <option :value="null">All categories</option>
                        <option v-for="category in categoryOptions" :key="'cat-' + category.id" :value="category.id">{{ category.title }} ({{ category.count }})</option>
                    </b-select>
                    <b-input v-model="search" placeholder="Search courses..." icon="search"/>
                </div>

                <div class="has-text-grey mt-3" v-if="!browseCourses.length"><i class="far fa-info-circle mr-2"></i>No courses match.</div>
                <div class="course-row" v-for="course in browseCourses" :key="'browse-' + course.id">
                    <div class="course-info">
                        <div class="course-title-row">
                            <div class="course-title">{{ course.title }}</div>
                            <span class="rec-badge" v-if="isRecommended(course)">Recommended</span>
                        </div>
                        <div class="course-org" v-if="course.organization">{{ course.organization.title }}</div>
                    </div>
                    <div class="course-actions">
                        <span class="in-package" v-if="instanceCount(course)">In bundle<template v-if="instanceCount(course) > 1"> &times;{{ instanceCount(course) }}</template></span>
                        <b-button size="is-small" @click="showInfo(course)"><i class="far fa-circle-info mr-2"></i>Info</b-button>
                        <b-button type="is-primary" size="is-small" :disabled="atLimit(course) || fullyAllocated" @click="choose(course)"><i class="fas fa-plus mr-2"></i>Add</b-button>
                    </div>
                </div>
            </div>
        </div>

        <div class="step-actions">
            <b-button type="is-text" @click="$emit('back')"><i class="fas fa-arrow-left mr-2"></i>Back</b-button>
            <b-button type="is-primary" size="is-medium" :disabled="!fullyAllocated" @click="$emit('continue')">Continue to Payment<i class="fas fa-arrow-right ml-2"></i></b-button>
        </div>

        <!-- Course information, as in the catalog -->
        <b-modal v-model="infoOpen" :width="720" scroll="keep">
            <div class="card" v-if="infoCourse">
                <div class="card-content info-content">
                    <h4 class="title is-5 mb-1">{{ infoCourse.title }}</h4>
                    <div class="modal-org" v-if="infoCourse.organization">{{ infoCourse.organization.title }}</div>

                    <div class="has-text-grey mt-5" v-if="infoLoading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading course information...</div>
                    <template v-else-if="infoDetail">
                        <div class="mt-5" v-if="infoDetail.description">
                            <h5 class="info-header"><i class="far fa-book-open fa-fw mr-2"></i>About This Course</h5>
                            <div v-html="infoDetail.description" class="info-text content"></div>
                        </div>

                        <div class="mt-5" v-if="infoGoals.length">
                            <h5 class="info-header"><i class="far fa-bullseye-arrow fa-fw mr-2"></i>Course Goals</h5>
                            <ul class="info-text">
                                <li v-for="(goal, i) in infoGoals" :key="'goal-' + i">{{ goal }}</li>
                            </ul>
                        </div>

                        <div class="mt-5" v-if="infoObjectives.length">
                            <h5 class="info-header"><i class="far fa-diagram-cells fa-fw mr-2"></i>Objectives</h5>
                            <p class="info-text mb-2">During enrollment setup you will build a personalized study plan by selecting from these objectives:</p>
                            <ul class="info-text">
                                <li v-for="(objective, i) in infoObjectives" :key="'obj-' + i">{{ objective }}</li>
                            </ul>
                        </div>

                        <div class="mt-5" v-else-if="infoDetail.outline && infoDetail.outline.length">
                            <h5 class="info-header"><i class="far fa-diagram-cells fa-fw mr-2"></i>Course Outline</h5>
                            <ul class="info-text">
                                <li v-for="item in infoDetail.outline" :key="'ol-' + item.id">{{ item.title }}</li>
                            </ul>
                        </div>
                    </template>
                    <div class="has-text-grey mt-5" v-else><i class="far fa-info-circle mr-2"></i>No additional information is available for this course.</div>

                    <div class="is-flex is-justify-content-space-between mt-5">
                        <b-button @click="infoOpen = false">Close</b-button>
                        <b-button type="is-primary" :disabled="atLimit(infoCourse) || fullyAllocated" @click="infoOpen = false; choose(infoCourse)"><i class="fas fa-plus mr-2"></i>Add to Bundle</b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Customize: clock hours + name, then add -->
        <b-modal v-model="customizing" :width="560" scroll="keep">
            <div class="card" v-if="chosen">
                <div class="card-content">
                    <h4 class="title is-5 mb-1">{{ chosen.title }}</h4>
                    <div class="modal-org" v-if="chosen.organization">{{ chosen.organization.title }}</div>

                    <b-field label="Clock hours from your bundle" class="mt-5" style="max-width: 220px">
                        <b-select v-model="chosenHours" expanded>
                            <option v-for="hours in hourOptionsFor(chosen)" :key="'ch-' + hours" :value="hours">{{ hours }} hours</option>
                        </b-select>
                    </b-field>

                    <!-- allow_custom_entry courses (e.g. Educational Book
                         Review) are named in the course setup wizard, not
                         here — same rule as the catalog checkout -->
                    <div v-if="namedInSetup" class="mt-5">
                        <b-message type="is-info">
                            <i class="far fa-tag mr-2"></i>You'll name this course during course setup — each enrollment gets its own focus (like the book you're reviewing) when you begin.
                        </b-message>
                    </div>
                    <div v-else-if="namingActive" class="mt-5">
                        <div class="has-text-weight-bold mb-2">Course Name</div>
                        <p class="is-size-7 has-text-grey mb-3">Choose the option(s) that complete the name of this enrollment. Each enrollment must have a unique name.</p>
                        <div class="columns">
                            <div class="column is-6" v-if="suffix1Options.length">
                                <b-select v-model="custom1" expanded>
                                    <option value="" disabled>Select an option...</option>
                                    <option v-for="option in suffix1Options" :value="option" :key="option">{{ option }}</option>
                                </b-select>
                            </div>
                            <div class="column is-6" v-if="suffix2Options.length">
                                <b-select v-model="custom2" expanded>
                                    <option value="" disabled>Select an option...</option>
                                    <option v-for="option in suffix2Options" :value="option" :key="option">{{ option }}</option>
                                </b-select>
                            </div>
                        </div>
                    </div>
                    <div v-else class="mt-5">
                        <b-field>
                            <template #label>
                                Focus <span class="has-text-grey has-text-weight-normal is-size-7">(optional — added after the course name)</span>
                            </template>
                            <b-input v-model="customText" :placeholder="needsDistinct ? 'Required — you already have &quot;' + chosen.title + '&quot;' : 'e.g. Biology, Assessment, Spring Semester'"/>
                        </b-field>
                    </div>

                    <b-message type="is-danger" v-if="!namedInSetup && proposedName && nameTaken" class="mt-3">
                        <i class="far fa-exclamation-triangle mr-2"></i>You already have a course named <strong>{{ proposedName }}</strong>. Please choose a different name.
                    </b-message>
                    <b-message type="is-info" v-else-if="!namedInSetup && proposedName" class="mt-3">
                        <i class="far fa-tag mr-2"></i>This enrollment will be named: <strong>{{ proposedName }}</strong>
                    </b-message>

                    <div class="is-flex is-justify-content-space-between mt-5">
                        <b-button @click="customizing = false">Cancel</b-button>
                        <b-button type="is-primary" :disabled="!canAdd" @click="confirmAdd"><i class="fas fa-plus mr-2"></i>Add to Bundle</b-button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import {mapActions, mapGetters, mapMutations} from "vuex";
    import {client as http} from "@/http_client";
    import {COURSE_HOUR_OPTIONS, MAX_INSTANCES_PER_COURSE, isPackageCourse, packageHourCap} from "@/constants/packages";
    import PackageProgress from "../PackageProgress";

    export default {
        name: "StepCourses",
        components: {PackageProgress},
        props: {
            courses: {type: Array, default: () => []},
            loading: {type: Boolean, default: false}
        },
        data: function() {
            return {
                hourOptions: COURSE_HOUR_OPTIONS,
                categoryFilter: null,
                search: '',
                customizing: false,
                chosen: null,
                chosenHours: COURSE_HOUR_OPTIONS[0],
                custom1: '',
                custom2: '',
                customText: '',
                infoOpen: false,
                infoCourse: null,
                infoDetail: null,
                infoLoading: false
            };
        },
        computed: {
            ...mapGetters(['enrollments', 'pkgTargetHours', 'pkgAllocatedHours', 'pkgRemainingHours', 'pkgSelections', 'pkgFocusCategories']),
            focusCategoryIds() {
                return new Set(this.pkgFocusCategories.map(c => c.id));
            },
            // Categories represented in the catalog, with course counts
            categoryOptions() {
                const byId = {};
                for (const course of this.courses) {
                    for (const category of (course.categories || [])) {
                        if (!byId[category.id]) byId[category.id] = {id: category.id, title: category.title, count: 0};
                        byId[category.id].count++;
                    }
                }
                return Object.values(byId).sort((a, b) => a.title.localeCompare(b.title));
            },
            browseCourses() {
                let list = this.courses;
                if (this.categoryFilter !== null) {
                    list = list.filter(c => (c.categories || []).some(cat => cat.id === this.categoryFilter));
                }
                if (this.search.trim()) {
                    const q = this.search.trim().toLowerCase();
                    list = list.filter(c => c.title.toLowerCase().includes(q));
                }
                return list;
            },
            // A minimal suggested set from the focus answers
            suggested() {
                return this.courses.filter(c => this.isRecommended(c)).slice(0, 4);
            },
            fullyAllocated() {
                return this.pkgTargetHours > 0 && this.pkgRemainingHours === 0;
            },
            statusLabel() {
                if (this.pkgRemainingHours > 0) return this.pkgRemainingHours + ' hours left to allocate';
                if (this.pkgRemainingHours < 0) return Math.abs(this.pkgRemainingHours) + ' hours over — remove some';
                return 'Fully allocated ✓';
            },
            statusClass() {
                if (this.pkgRemainingHours < 0) return 'is-over';
                if (this.pkgRemainingHours === 0) return 'is-full';
                return '';
            },
            // Names already in use anywhere: existing enrollments (their custom
            // name, or the course title) plus what's in this package
            takenNames() {
                const names = new Set();
                for (const enrollment of (this.enrollments || [])) {
                    if (enrollment.is_deleted) continue;
                    const name = (enrollment.custom_name || (enrollment.courses && enrollment.courses.title) || '').trim().toLowerCase();
                    if (name) names.add(name);
                }
                for (const selection of this.pkgSelections) {
                    names.add(selection.name.trim().toLowerCase());
                }
                return names;
            },
            customNaming() {
                const cfg = this.chosen && this.chosen.self_directed_config;
                const naming = cfg && cfg.custom_naming;
                return (naming && !naming.allow_custom_entry) ? naming : null;
            },
            // allow_custom_entry courses take their name in the setup wizard;
            // the builder collects no name and skips the uniqueness rules
            namedInSetup() {
                const cfg = this.chosen && this.chosen.self_directed_config;
                const naming = cfg && cfg.custom_naming;
                return !!(naming && naming.allow_custom_entry);
            },
            suffix1Options() {
                const n = this.customNaming;
                return (n && Array.isArray(n.custom1_options)) ? n.custom1_options : [];
            },
            suffix2Options() {
                const n = this.customNaming;
                return (n && Array.isArray(n.custom2_options)) ? n.custom2_options : [];
            },
            namingActive() {
                return !!(this.suffix1Options.length || this.suffix2Options.length);
            },
            namingComplete() {
                if (!this.namingActive) return true;
                if (this.suffix1Options.length && !this.custom1) return false;
                if (this.suffix2Options.length && !this.custom2) return false;
                return true;
            },
            // Free-text naming: a qualifier is mandatory once the bare course
            // name is taken
            needsDistinct() {
                if (!this.chosen || this.namingActive || this.namedInSetup) return false;
                return this.takenNames.has(this.chosen.title.trim().toLowerCase());
            },
            proposedName() {
                if (!this.chosen || !this.namingComplete) return '';
                if (this.namedInSetup) return this.chosen.title.trim();
                if (this.namingActive) {
                    const base = ((this.customNaming.prefix || this.chosen.title) || '').trim();
                    return [base, this.custom1.replace('N/A', ''), this.custom2.replace('N/A', '')]
                        .join(' ').replace(/\s+/g, ' ').trim();
                }
                return (this.chosen.title + ' ' + this.customText).replace(/\s+/g, ' ').trim();
            },
            nameTaken() {
                if (!this.proposedName || this.namedInSetup) return false;
                return this.takenNames.has(this.proposedName.toLowerCase());
            },
            canAdd() {
                return !!this.proposedName && this.namingComplete && !this.nameTaken && !this.atLimit(this.chosen);
            },
            infoGoals() {
                return this.sdList('goals');
            },
            infoObjectives() {
                return this.sdList('objectives');
            }
        },
        methods: {
            ...mapActions(['getEnrollments']),
            ...mapMutations(['addPkgCourse', 'setPkgCourseHours', 'removePkgCourse']),
            // TODO(api): recommendation should come from real course metadata;
            // until then, loosely match the chosen focus against category titles
            // A course is recommended when it sits in any chosen focus
            // category — matched by category id, never by title text
            isRecommended(course) {
                return (course.categories || []).some(category => this.focusCategoryIds.has(category.id));
            },
            instanceCount(course) {
                if (!course) return 0;
                return this.pkgSelections.filter(s => s.id === course.id).length;
            },
            // Package-certification courses are repeatable with unique names
            maxInstances(course) {
                if (!course) return 1;
                return isPackageCourse(course) ? MAX_INSTANCES_PER_COURSE : 1;
            },
            atLimit(course) {
                return this.instanceCount(course) >= this.maxInstances(course);
            },
            // Hour choices are limited to what the course can actually award
            // (its largest package-certification config)
            hourOptionsFor(course) {
                if (!course) return this.hourOptions;
                const cap = packageHourCap(course);
                return this.hourOptions.filter(hours => hours <= cap);
            },
            courseById(id) {
                return this.courses.find(course => course.id === id);
            },
            choose(course) {
                const options = this.hourOptionsFor(course);
                const min = options[0];
                const max = options[options.length - 1];
                const remaining = this.pkgRemainingHours > 0 ? this.pkgRemainingHours : min;
                this.chosen = course;
                this.chosenHours = Math.max(min, Math.min(max, Math.round(remaining / min) * min));
                this.custom1 = '';
                this.custom2 = '';
                this.customText = '';
                this.customizing = true;
            },
            // Full course record (description, outline, sd config), same as
            // the catalog's enroll page
            showInfo(course) {
                this.infoCourse = course;
                this.infoDetail = null;
                this.infoLoading = true;
                this.infoOpen = true;
                http.get('app/courses/new/' + course.id).then(response => {
                    this.infoDetail = response.data;
                    this.infoLoading = false;
                }).catch(() => {
                    this.infoLoading = false;
                });
            },
            // goals/objectives are {text} objects in self_directed_config
            sdList(key) {
                const detail = this.infoDetail;
                const cfg = detail && detail.self_directed_config;
                const list = cfg && Array.isArray(cfg[key]) ? cfg[key] : [];
                return list.map(item => (item && item.text) ? item.text : item).filter(Boolean);
            },
            confirmAdd() {
                if (!this.canAdd) return;
                const selection = {
                    id: this.chosen.id,
                    title: this.chosen.title,
                    name: this.proposedName,
                    named_in_setup: this.namedInSetup,
                    organization: this.chosen.organization ? this.chosen.organization.title : null,
                    hours: this.chosenHours
                };
                // Unmount the modal content before the selection joins the
                // taken-name set — otherwise the closing modal briefly flags
                // the new name as conflicting with itself
                this.customizing = false;
                this.chosen = null;
                this.addPkgCourse(selection);
            }
        },
        mounted() {
            // Needed for the name-conflict check
            if (!this.enrollments) this.getEnrollments();
        }
    };
</script>

<style lang="scss" scoped>
    .multi-note {
        margin-top: 0.9rem;
        background: #f2f7fc;
        border-radius: 6px;
        padding: 0.8rem 1rem;
        font-size: 0.85rem;
        color: #666d7a;

        i {
            color: #2e5f8f;
        }

        strong {
            color: #33475b;
        }
    }

    .alloc-status {
        font-size: 0.85rem;
        font-weight: bold;
        color: #7a8a9d;

        &.is-full {
            color: #2e7d4f;
        }

        &.is-over {
            color: #c0392b;
        }
    }

    .selected-list {
        border: 1px solid #d6e4f2;
        background: #f7fafd;
        border-radius: 6px;
        padding: 0.4rem 1rem;
        margin-bottom: 1.2rem;

        .selected-row {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 0.6rem 0;
            border-bottom: 1px solid #e1e9f2;

            &:last-child {
                border-bottom: none;
            }

            .selected-info {
                min-width: 0;
                flex: 1;

                .selected-name {
                    font-size: 0.95rem;
                    font-weight: bold;
                    color: #33475b;
                }

                .selected-org {
                    font-size: 0.75rem;
                    color: #7a8a9d;
                    margin-top: 1px;
                }
            }

            .selected-actions {
                display: flex;
                align-items: center;
                gap: 12px;
                flex-shrink: 0;
            }
        }
    }

    .browse-section {
        margin-top: 1.4rem;

        .browse-heading {
            font-size: 0.95rem;
            font-weight: 900;
            color: #33475b;
            margin-bottom: 0.5rem;

            i {
                color: #2e5f8f;
            }
        }

        .browse-controls {
            display: flex;
            gap: 12px;
            margin-bottom: 0.5rem;
            flex-wrap: wrap;

            > * {
                flex: 1;
                min-width: 200px;
            }
        }
    }

    .course-row {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 0.7rem 0;
        border-bottom: 1px solid #eef1f5;

        &:last-child {
            border-bottom: none;
        }

        .course-info {
            min-width: 0;
            flex: 1;

            .course-title-row {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
            }

            .course-title {
                font-size: 0.95rem;
                font-weight: bold;
                color: #33475b;
            }

            .rec-badge {
                background: #f2f7fc;
                color: #2e5f8f;
                font-size: 0.65rem;
                font-weight: 900;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                padding: 3px 8px;
                border-radius: 999px;
                flex-shrink: 0;
            }

            .course-org {
                font-size: 0.75rem;
                color: #7a8a9d;
                margin-top: 1px;
            }
        }

        .course-actions {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;

            .in-package {
                font-size: 0.75rem;
                font-weight: bold;
                color: #2e7d4f;
                background: #e7f4ec;
                padding: 3px 10px;
                border-radius: 999px;
                white-space: nowrap;
            }
        }
    }

    .modal-org {
        font-size: 0.8rem;
        color: #7a8a9d;
    }

    .info-content {
        max-height: 80vh;
        overflow-y: auto;

        .info-header {
            font-size: 0.95rem;
            font-weight: 900;
            color: #33475b;
            margin-bottom: 0.5rem;

            i {
                color: #2e5f8f;
            }
        }

        .info-text {
            font-size: 0.9rem;
            color: #4a4a4a;

            li {
                margin: 0.25rem 0 0.25rem 1.2rem;
                list-style: disc;
            }
        }
    }
</style>
